<template>
    <div spellcheck="false" class="PromptEditor">
        <!--Title-->
        <div v-if="tag" class="mb-8 centerY justify-space-between">
            <v-form
                ref="form"
                v-model="formValid"
                v-if="isEditTag && isEdit"
                class="centerY"
            >
                <div :style="{ width: `${showBtnDelete ? '374px' : '406px'}` }">
                    <v-text-field
                        class="input-custom"
                        v-model="tagPrompt"
                        :rules="[formRules.ruleRequired, ruleDuplicateTag]"
                        @keydown.enter="submit"
                        @focusout="submit"
                    ></v-text-field>
                </div>
                <div
                    class="btn-icon ml-6"
                    style="padding-top: 6px"
                    @click="submit"
                >
                    <img
                        src="/img/scenario/save_grey.svg"
                        alt="icon-save-title"
                        style="transform: scale(1.15);"
                    />
                </div>
            </v-form>
            <div v-else class="centerY">
                <div :class="isFallback ? 'tag-fallback' : 'tag-title'" :title="tagPrompt">{{ tagPrompt }}</div>
                <div
                    class="btn-icon ml-6"
                    @click="isEditTag = true"
                    v-if="isEdit && !isFallback"
                >
                    <img src="/img/scenario/edit.svg" alt="icon-edit"/>
                </div>
            </div>
            <!--Button delete or prompt-->
            <div
                v-if="isEdit && showBtnDelete && !isFallback"
                class="btn-icon ml-6"
                @click="deleteOrPrompt"
            >
                <img
                    src="/img/scenario/delete-new_red.svg"
                    alt="icon-delete-or-prompt"
                />
            </div>
        </div>
        <!--Prompt-->
        <div spellcheck="false" :class="errorMessage ? 'error-input' : ''">
            <v-textarea
                v-model="valueInner"
                @focusout="focusout"
                @keydown.tab="tab($event)"
                @keyup="saveMousePosition"
                @focusin="saveMousePosition"
                @click="saveMousePosition"
                ref="input"
                :readonly="!isEdit"
                :placeholder="$f.tc('enterPrompt')"
                class="textarea-custom"
                spellcheck="false"
                :rows="rows"
            ></v-textarea>
        </div>
        <div v-if="errorMessage" class="error-message">
            <img
                src="/img/warning.svg"
                alt="icon-warning"
                style="transform: scale(0.8); margin-right: 2px"
            />
            {{ errorMessage }}
        </div>
        <v-layout v-else>
            <div
                v-for="(variable, i) in filterVariableList"
                :key="i"
                class="variable"
                @click="selectVariable(variable)"
            >
                {{ variable.name }}
            </div>
        </v-layout>
        <div class="centerY justify-space-between mt-10" style="height: 26px">
            <div class="prompt-actions">
                <div v-if="!isFallback && !isRePrompt" class="btn-toggle-conditions" @click="toggleConditions">
                    <img
                        :src="isShowConditions ? '/img/scenario/eye-off.svg' : '/img/scenario/eye-line.svg'"
                        alt="icon-toggle-conditions"
                        class="mr-1"
                    >
                    {{ isShowConditions ? $f.tc('hide') : $f.tc('show') }} {{ $t('condition') }}
                </div>
                <div
                    v-if="useGuardrail && valueInner.trim().length > 0 && isEdit"
                    class="btn-rewrite"
                    @click="openRephrasePromptDialog"
                >
                    <div class="icon-rewrite"></div>
                    {{ $f.tc('rephrase') }}
                </div>
                <div v-if="valueInner.trim().length > 0" class="btn-copy-prompt" @click="copyPrompt">
                    <div class="icon-copy-prompt"></div>
                    {{ $f.tc('copy') }}
                </div>
            </div>
            <div class="length-prompt">{{ `${valueInner.length} ${$t('chars')} ` }}</div>
        </div>
        <ConditionEditor
            v-if="isShowConditions && useConditions"
            ref="ConditionEditor"
            :items.sync="conditionList"
            @save="saveConditions"
            class="mt-8"
        ></ConditionEditor>
        <ScenarioRephrasePrompt
            @applyPrompt="applyPrompt"
            ref="ScenarioRephrasePrompt"
        ></ScenarioRephrasePrompt>
    </div>
</template>

<script>
import lodash from "lodash";
import {mapState} from "vuex";
import ScenarioService from "@/services/scenario.service";
import FormMixin from "@/modules/fptai/mixins/FormMixin";
import ScenarioRephrasePrompt from "@/views/Scenario/ScenarioRephrasePrompt.vue";
import ConditionEditor from "@/views/Scenario/NodeEditor/common/ConditionEditor.vue";

export default {
    name: "PromptEditor",
    components: {ConditionEditor, ScenarioRephrasePrompt},
    mixins: [FormMixin],
    props: {
        value: String,
        showBtnDelete: Boolean,
        tag: String,
        rows: {
            type: Number,
            default: 5
        },
        promptList: Array,
        showConditions: {
            type: Boolean,
            default: false
        },
        useConditions: {
            type: Boolean,
            default: true
        },
        isFallback: {
            type: Boolean,
            default: false
        },
        conditionList: {
            type: Array,
            default: () => [],
        },
        isRePrompt: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            tagPrompt: lodash.unescape(this.tag),
            valueInner: lodash.unescape(this.value),
            mouseStart: 0,
            searchKey: "",
            errorMessage: "",
            isEditTag: false,
            isShowConditions: false,
        };
    },
    computed: {
        ...mapState({
            variableList: state => state.scenario.variableList,
            useGuardrail: state => state.scenario.useGuardrail,
        }),
        filterVariableList() {
            let list = [];
            if (this.searchKey) {
                list = this.variableList.filter(item =>
                    item.name.startsWith(this.searchKey)
                );
            } else {
                let mouseStart = this.mouseStart;
                let value = this.valueInner;
                if (
                    mouseStart > 1 &&
                    value[mouseStart - 1] === "{" &&
                    value[mouseStart - 2] === "{"
                ) {
                    list = this.variableList;
                }
            }
            return list.filter((item, i) => i < 5);
        },
        isEdit() {
            return (
                ScenarioService.isEditable() && ScenarioService.isModeEditing()
            );
        }
    },
    mounted() {
        this.inspectVariable();
    },
    methods: {
        focusout() {
            this.$emit("input", this.valueInner);
            if (this.value !== this.valueInner) {
                this.$emit("save");
            }
        },
        ruleDuplicateTag(value) {
            if (this.promptList.length === 0) return true;
            if (value.trim() === this.tag.trim()) return true;
            let check = !this.promptList.find(
                i => i.tag.trim() === value.trim()
            );
            return check || this.$f.tc("formRules.fieldDuplicate");
        },
        submitValidForm() {
            if (this.tagPrompt.trim().toLowerCase() === 'fallback') {
                this.$appService.toastError(this.$f.tc('tagPromptInvalid'))
                return
            }

            this.saveTitle();
        },
        saveTitle() {
            this.isEditTag = false;
            if (this.tag !== this.tagPrompt) {
                this.$emit("updateTag", this.tagPrompt);
            }
        },
        tab(e) {
            if (this.filterVariableList.length > 0) {
                e.preventDefault();
                this.selectVariable(this.filterVariableList[0]);
            }
        },
        selectVariable(variable) {
            let mouseStart = this.mouseStart;
            let value = this.valueInner;
            for (let i = mouseStart; i >= 0; i--) {
                if (value[i] === "}") {
                    break;
                }
                if (value[i] === "{") {
                    this.searchKey = value.substring(i + 1, mouseStart);
                    let j = -1;
                    for (j = mouseStart; j < value.length - 1; j++) {
                        if (value[j] === "}" && value[j + 1] === "}") {
                            break;
                        }
                    }
                    let append;
                    if (j < value.length - 1) {
                        append = value.substring(j);
                    } else {
                        let nextSpaceIndex = value.indexOf(" ", mouseStart);
                        if (nextSpaceIndex > -1) {
                            append = "}}" + value.substring(nextSpaceIndex);
                        } else {
                            append = "}}";
                        }
                    }

                    value = value.substring(0, i + 1) + variable.name + append;
                    this.valueInner = value;
                    let el = this.$refs.input;
                    el.focus();
                    if (append !== "}}") {
                        setTimeout(() => {
                            el.setSelectionRange(mouseStart, mouseStart);
                        }, 0);
                    }
                    break;
                }
            }
        },
        saveMousePosition() {
            let mouseStart = this.$refs.input.selectionStart;
            let value = this.valueInner;
            let searchKey = "";
            for (let i = mouseStart - 1; i >= 0; i--) {
                if (value[i] === "}") {
                    break;
                }
                if (value[i] === "{") {
                    if (i > 0 && value[i - 1] === "{") {
                        searchKey = value.substring(i + 1, mouseStart);
                    }
                    break;
                }
            }
            this.mouseStart = mouseStart;
            this.searchKey = searchKey;
            this.inspectVariable();
        },
        inspectVariable() {
            let valueInner = this.valueInner;
            let i = 0,
                j = 0;
            while (i < valueInner.length) {
                i = valueInner.indexOf("{{", i);
                if (i > -1) {
                    j = valueInner.indexOf("}}", i);
                    if (j > -1) {
                        let name = valueInner.substring(i + 2, j);
                        if (
                            !this.variableList.find(
                                variable => variable.name === name
                            )
                        ) {
                            this.errorMessage = `\"${name}\" does not exist`;
                            return;
                        }
                        i = j + 2;
                    } else {
                        i = valueInner.length;
                    }
                } else {
                    i = valueInner.length;
                }
            }
            this.errorMessage = "";
        },
        deleteOrPrompt() {
            this.$emit("deleteOrPrompt");
        },
        copyPrompt() {
            this.$utils.copyToClipboard(this.valueInner, this.$f.tc("copied"));
        },
        openRephrasePromptDialog() {
            this.$refs.ScenarioRephrasePrompt.open(this.valueInner)
        },
        toggleConditions() {
            this.isShowConditions = !this.isShowConditions
            this.$emit('toggleConditions', this.isShowConditions)
        },
        saveConditions() {
            this.$emit('updateCondition', this.conditionList)
        },
        applyPrompt(promptsList) {
            this.$emit('addPromptWithContent', promptsList)
            this.focusout()
        }
    },
    watch: {
        value() {
            this.valueInner = lodash.unescape(this.value);
        },
        tag() {
            this.tagPrompt = lodash.unescape(this.tag);
        },
        valueInner: "inspectVariable",
        variableList: "inspectVariable",
        isEdit() {
            if (!this.isEdit) {
                this.isEditTag = this.isEdit ? this.isEditTag : false;
            }
        },
        showConditions: {
            handler: function () {
                this.isShowConditions = this.showConditions || false
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">
.variable {
    height: 20px;
    padding: 0 5px;
    display: flex;
    align-items: center;
    border-radius: 5px;
    border: 1px solid #b2b4c6;
    color: #b2b4c6;
    margin-right: 5px;
    cursor: pointer;
}

.error-message {
    color: red;
    height: 24px;
    line-height: 24px;
    display: flex;
    align-items: center;
    font-size: 13px;
    font-family: "open sans", serif;
}

.tag-title, .tag-fallback {
    padding: 2px 8px;
    border-radius: 5px;
    border: 1px solid #d9d9d9;
    color: #727885;
    font-size: 13px;
    max-width: 220px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-family: 'open sans bold', serif;
}

.tag-fallback {
    border-color: #727885 !important;
    background: #727885 !important;
    color: #ffffff !important;
}

.input-custom {
    position: relative;

    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #ffffff;
            border-radius: 5px;
            border: 1px solid #d9d9d9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before,
            &:after {
                border: none !important;
            }
        }
    }
}

.btn-icon {
    border-radius: 5px !important;
    height: 26px !important;
    width: 26px !important;
}

.textarea-custom {
    ::v-deep {
        .v-input__slot {
            padding: 0;
            background: #f2f2f2cc;
            border-radius: 5px;
            border: none;
            outline: none;
            font-size: 14px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;

            &:before,
            &:after {
                border: none !important;
            }
        }

        .v-messages {
            min-height: 0 !important;
        }
    }
}

.prompt-actions {
    display: flex;
    align-items: center;
    gap: 6px;

    img {
        height: 14px;
        margin-right: 3px;
    }

    .btn-rewrite, .btn-copy-prompt, .btn-toggle-conditions {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px 8px;
        border: 1px solid #d9d9d9;
        border-radius: 5px;
        cursor: pointer;
        font-size: 13px;
        font-family: "open sans semi", serif;
        color: #727885;
        user-select: none;

        &:hover {
            background: #f2f2f2;
        }
    }

    .btn-toggle-conditions {
        background: #0052CC !important;
        color: #FFF !important;
        border-color: #0052CC !important;
    }

    .icon-rewrite, .icon-copy-prompt {
        height: 15px;
        width: 15px;
        margin-right: 4px;
    }

    .icon-copy-prompt {
        height: 18px;
        width: 18px;
    }

    .icon-rewrite:before {
        content: url("/img/scenario/rewrite.svg");
    }

    .btn-rewrite:hover {
        color: #F7722F;
        border-color: #ffdcc1 !important;

        .icon-rewrite:before {
            content: url("/img/scenario/rewrite_yellow.svg");
        }

        background: #ffdcc1;
    }

    .icon-copy-prompt:before {
        content: url("/img/scenario/copy.svg");
        height: 14px;
        width: 15px;
        margin-right: 4px;
    }

    .btn-copy-prompt:hover {
        color: #0052cc;
        border-color: #EEF6FF !important;

        .icon-copy-prompt:before {
            content: url("/img/scenario/copy_blue.svg");
        }

        background: #EEF6FF;
    }
}

.length-prompt {
    font-family: "open sans", serif;
    font-size: 13px;
    color: #8f97a6;
}

::v-deep {
    .v-text-field {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .v-text-field__details {
        position: absolute;
        right: 12px;
        top: 8px;
    }

    .error--text {
        .v-input__slot {
            border: 1px solid #ff5252 !important;
        }
    }

    textarea {
        padding: 8px 10px !important;
        font-family: 'open sans', serif !important;
    }
}
</style>
